import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import { useGoogleLogin } from 'api/auth/googleLogin'
import { useTwitterLogin } from 'api/auth/twitterLogin'
import { Button } from 'components/Button/Button'
import CheckboxField from 'components/Form/CheckboxField/CheckboxField'
import InputField from 'components/Form/InputField/InputField'
import { addToast } from 'components/Toast/Toast'
import VerificationModalWrapper from 'features/VerificationCode/VerificationModalWrapper'
import { Icons } from 'helpers/icons'
import { type UserProfile } from 'models'
import { RecoverAccount } from 'pages/auth/view/RecoverAccount/RecoverAccount'
import { Register, registrationOpen } from 'pages/auth/view/Register/Register'
import { loginUser } from 'services/endpoints/auth'
import { useSetUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { useLocalStorageSetAccessToken } from 'utils'
import '../_auth.scss'

interface LoginFormProps {
  fromUser?: boolean
  setActivePanel?: any
  onSuccessInModal?: () => void
  onInputFocus?: () => void
  showNormalLoginTitle?: boolean
}

type Step = 'login' | 'verify' | 'preregistered'
type TwoFactorType = 'qr_authenticator' | 'sms'

interface TwoFactorChallengeResponse {
  requires_two_factor: true
  challenge: string
  two_factor_auth_type: TwoFactorType
  message?: string
}

interface CompletedLoginResponse {
  requires_two_factor?: false
  token: string
  userProfile: UserProfile
  message?: string
}

type LoginResponse = TwoFactorChallengeResponse | CompletedLoginResponse

type ModalAuthPanel =
  | 'login'
  | 'register'
  | 'forgotPassword'

export const LoginForm = ({
  fromUser = false,
  setActivePanel,
  onSuccessInModal,
  onInputFocus,
  showNormalLoginTitle = false
}: LoginFormProps) => {
  const { t } = useTranslation()

  const [modalAuthPanel, setModalAuthPanel] =
    useState<ModalAuthPanel>('login')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [pendingSocial2FA] = useState<{
    challenge: string
    two_factor_auth_type: TwoFactorType
  } | null>(() => {
    const stored = sessionStorage.getItem('pending_social_2fa')

    if (!stored) return null

    try {
      const data = JSON.parse(stored)

      if (
        typeof data.challenge !== 'string' ||
        data.challenge.length !== 64 ||
        !['qr_authenticator', 'sms'].includes(data.two_factor_auth_type) ||
        typeof data.created_at !== 'number' ||
        Date.now() - data.created_at < 0 ||
        Date.now() - data.created_at >= 5 * 60 * 1000
      ) {
        return null
      }

      return {
        challenge: data.challenge,
        two_factor_auth_type: data.two_factor_auth_type
      }
    } catch {
      return null
    }
  })

  const [step, setStep] = useState<Step>(
    pendingSocial2FA ? 'verify' : 'login'
  )

  const [preregisteredMessage, setPreregisteredMessage] = useState('')

  const [twoFactorChallenge, setTwoFactorChallenge] =
    useState<string | null>(pendingSocial2FA?.challenge ?? null)

  const [twoFactorType, setTwoFactorType] =
    useState<TwoFactorType | null>(
      pendingSocial2FA?.two_factor_auth_type ?? null
    )
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [passwordInputType, setPasswordInputType] = useState(true)
  const [isMobileView, setIsMobileView] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [loginAttempted, setLoginAttempted] = useState({
    email: false,
    password: false
  })

  const openTwitterLoginMutation = useTwitterLogin()
  const getGoogleRedirectURL = useGoogleLogin()

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

useEffect(() => {
  if (step !== 'preregistered') return

  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}, [step])

  const setLoggedInUser = (user: UserProfile) => {
    useSetUserProfile(user)
    queryClient.setQueryData(['user-profile'], user)

    if (user?.username && user?.id) {
      useProfileCacheStore.getState().setProfile(
        user.username,
        user as any,
        user.id
      )
    }
  }

const completeLogin = (
  token: string,
  userProfile: UserProfile
) => {
  if (fromUser) {
    onSuccessInModal?.()
  }

  useLocalStorageSetAccessToken(token)
  setLoggedInUser(userProfile)

  if (fromUser) {
    return
  }

  if (
    userProfile.role === 'model' &&
    userProfile.username
  ) {
    navigate(`/${userProfile.username}/`, {
      replace: true
    })
    return
  }

  navigate('/following', {
    replace: true
  })
}

  const submitForm = async () => {
    const trimmedEmail = email.trim()

    if (!trimmedEmail || !password) {
      setLoginAttempted({
        email: !trimmedEmail,
        password: !password
      })
      return
    }

    if (isSubmitting) return

    setIsSubmitting(true)

try {
  const response = await loginUser({
    email: trimmedEmail,
    password,
    rememberMe
  })


  if (response.requires_two_factor === true) {

    setTwoFactorChallenge(response.challenge)
    setTwoFactorType(response.two_factor_auth_type)
    setStep('verify')
    return
  }


  if (!response.token || !response.userProfile) {
    throw new Error('Invalid login response.')
  }

  completeLogin(response.token, response.userProfile)
} catch (error) {
    const response = (error as any)?.response

    const status = response?.status
    const errorCode = response?.data?.code

    const message =
        (error as any)?.response?.data?.error?.message ??
        (error as any)?.response?.data?.message ??
        t('error:errorInvalidDataLogin')

  if (
      (status === 403 || errorCode === 403) &&
      message ===
          'Your account is already registered. You will be able to log in when AllFans launches.'
  ) {
    if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
    }

    setPreregisteredMessage(message)
    setStep('preregistered')
    return
    }

    setLoginAttempted({
        email: true,
        password: true
    })

    addToast('error', t(message))
} finally {
    setIsSubmitting(false)
}
  }

  const googleLogin = async () => {
    if (getGoogleRedirectURL.isLoading) return

    try {
      const url = await getGoogleRedirectURL.mutateAsync()
      window.location.href = url
    } catch {
      addToast('error', t('error:errorSomethingWentWrong'))
    }
  }

  const twitterLogin = async () => {
    if (openTwitterLoginMutation.isLoading) return
    openTwitterLoginMutation.mutate()
  }

  const handleBackToLogin = () => {
    setTwoFactorChallenge(null)
    setTwoFactorType(null)
    setStep('login')
  }

  const handleTwoFactorSuccess = (token: string, userProfile: UserProfile) => {
    setTwoFactorChallenge(null)
    setTwoFactorType(null)
    completeLogin(token, userProfile)
  }
if (fromUser && modalAuthPanel === 'register') {
  return (
    <Register
      fromSubscribeModal
      onBackToLogin={() => {
        setModalAuthPanel('login')
      }}
      onInputFocus={onInputFocus}
    />
  )
}

if (fromUser && modalAuthPanel === 'forgotPassword') {
  return (
    <RecoverAccount
      fromSubscribeModal
      setActivePanel={(panel: string) => {
        if (panel === 'login') {
          setModalAuthPanel('login')
        }
      }}
    />
  )
}
  return (
    <>
      {step === 'login' && (
        <form
          className='auth-login__form'
          autoComplete='on'
          onSubmit={e => {
            e.preventDefault()
            void submitForm()
          }}
        >
          <h1 className='auth-register__title'>
            {showNormalLoginTitle ? 'Login' : fromUser ? 'Login To Subscribe' : 'Login'}
          </h1>

          {(!isMobileView || fromUser) && (
            <>
              <div
                className={
                  fromUser
                    ? 'auth-login__social-row auth-login__social-row--subscribe-modal'
                    : undefined
                }
              >
                <Button
                  text={fromUser ? 'Continue' : t('continueTwitter')}
                  color='white'
                  customClass={`auth-login__twitter ${
                    fromUser ? 'auth-login__social-compact' : ''
                  } ${
                    !registrationOpen ? 'auth-login__social-disabled' : ''
                  }`}
                  hasCircleIcon={true}
                  circleIcon={Icons.twitter}
                  circleIconBg='twitter'
                  clickFn={registrationOpen ? twitterLogin : () => {}}
                  disabled={!registrationOpen}
                />

                <Button
                  text={fromUser ? 'Continue' : t('continueGoogle')}
                  color='white'
                  customClass={`auth-login__google ${
                    fromUser ? 'auth-login__social-compact' : ''
                  } ${
                    !registrationOpen ? 'auth-login__social-disabled' : ''
                  }`}
                  hasCircleIcon={true}
                  circleIcon={Icons.google}
                  circleIconBg='google'
                  clickFn={
                    registrationOpen
                      ? async () => await googleLogin()
                      : () => {}
                  }
                  disabled={!registrationOpen}
                />
              </div>

              <div className='divider'>
                <span>or Login via</span>
              </div>
            </>
          )}

          <div onFocusCapture={onInputFocus}>
            <InputField
              id='login-email'
              type='text'
              value={email}
              changeFn={setEmail}
              additionalProps={{
                name: 'username',
                autoComplete: 'username',
                placeholder: `${t('email')} / ${t('username')}`
              }}
              loginAttempted={loginAttempted.email}
            />
          </div>

          <div onFocusCapture={onInputFocus}>
            <InputField
              id='login-password'
              type={passwordInputType ? 'password' : 'text'}
              value={password}
              changeFn={setPassword}
              inputvisible={passwordInputType}
              swaptypeicon={true}
              swaptype={() => {
                setPasswordInputType(current => !current)
              }}
              additionalProps={{
                name: 'password',
                autoComplete: 'current-password',
                placeholder: t('password')
              }}
              loginAttempted={loginAttempted.password}
            />
          </div>

            {fromUser ? (
              <div className='auth-login__actions auth-login__actions--subscribe-modal'>
                <button
                  type='button'
                  className='auth-login__forgot-password'
                  onClick={() => {
                    setModalAuthPanel('forgotPassword')
                  }}
                >
                  {t('forgotPassword')}
                </button>
              </div>
            ) : (
            <div className='auth-login__actions'>
              <CheckboxField
                id='login-remember'
                label={t('rememberMe')}
                checked={rememberMe}
                changeFn={() => {
                  setRememberMe(current => !current)
                }}
              />

              <p
                className='auth-login__forgot-password'
                onClick={() => {
                    setActivePanel?.('forgetPassword')
                }}
              >
                {t('forgotPassword')}
              </p>
            </div>
          )}

          <div className='auth-login__action auth-login__action--split'>
            <button
              type='button'
              className='auth-login__back-btn'
              onClick={() => {
                if (fromUser) {
                  setModalAuthPanel('register')
                  return
                }

                setActivePanel?.('default')
              }}
            >
              {fromUser ? t('signup') : 'Back'}
            </button>

            <button
              type='submit'
              className='button button--blue submit-btn auth-login__submit-btn'
              disabled={isSubmitting}
            >
              {t('login')}
            </button>
          </div>
        </form>
      )}

      {step === 'verify' && twoFactorChallenge && twoFactorType && (
        <div className='verification-modal'>
          <VerificationModalWrapper
            challenge={twoFactorChallenge}
            twoFactorType={twoFactorType}
            showBack
            onBack={handleBackToLogin}
            onSuccess={handleTwoFactorSuccess}
          />
        </div>
      )}
      {step === 'preregistered' && (
    <div className='auth-register'>
        <h1 className='auth-register__title'>
            You're Pre-Registered!
        </h1>

        <div className='auth-register__form'>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: '1.5rem',
                    padding: '0rem 1rem 2rem 1rem'
                }}
            >
                <div
                    style={{
                        width: '4rem',
                        height: '4rem',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        color: '#2196F3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '2rem',
                        fontWeight: 600
                    }}
                >
                    ✓
                </div>

                <p
                    style={{
                        fontSize: '1.4rem',
                        lineHeight: 1.6,
                        color: '#696B73',
                        maxWidth: '35rem',
                        margin: 0
                    }}
                >
                    {preregisteredMessage}
                </p>

                <button
                    type='button'
                    className='button button--blue submit-btn'
                    style={{
                        width: '100%',
                        maxWidth: 'none',
                        marginTop: '1rem'
                    }}
                    onClick={() => {
                        setStep('login')
                        setPreregisteredMessage('')
                        setPassword('')
                        setLoginAttempted({
                            email: false,
                            password: false
                        })
                    }}
                >
                    OK
                </button>
            </div>
        </div>
    </div>
)}
    </>
  )
}